import { useRef, useState, useCallback, useEffect } from 'react';

interface UseHlsPlayerOptions {
  streamUrl: string | null;
  sasToken?: string | null;
}

interface UseHlsPlayerReturn {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  isLoading: boolean;
  error: string | null;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seek: (time: number) => void;
}

/**
 * Appends a SAS query string to a URL.
 * The sasToken starts with '?' so we strip that and merge with any existing query params.
 */
function appendSasToken(url: string, sasToken: string): string {
  const token = sasToken.startsWith('?') ? sasToken.slice(1) : sasToken;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${token}`;
}

export function useHlsPlayer({ streamUrl, sasToken }: UseHlsPlayerOptions): UseHlsPlayerReturn {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<import('hls.js').default | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cleanup function
  const destroyHls = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  }, []);

  // Initialize HLS.js
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !streamUrl) {
      destroyHls();
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Dynamic import for code splitting
    import('hls.js').then((HlsModule) => {
      const Hls = HlsModule.default;

      if (Hls.isSupported()) {
        destroyHls();

        // When a SAS token is provided, use fetchSetup and xhrSetup to append
        // it to every request URL (manifest + segments).
        const hlsConfig: Partial<import('hls.js').HlsConfig> = {
          enableWorker: true,
          lowLatencyMode: false,
        };

        if (sasToken) {
          const token = sasToken;
          // HLS.js defaults to XHR; xhrSetup rewrites the URL for every
          // manifest and segment request so the SAS token is included.
          hlsConfig.xhrSetup = (xhr: XMLHttpRequest, url: string) => {
            xhr.open('GET', appendSasToken(url, token), true);
          };
        }

        const hls = new Hls(hlsConfig);

        // SAS callbacks handle appending token to ALL requests (manifest + segments).
        // Do NOT pre-append SAS here — that would double-append on the manifest.
        hls.loadSource(streamUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsLoading(false);
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              case Hls.ErrorTypes.NETWORK_ERROR:
                // Recreate on fatal network error
                setError('errors.player.networkError');
                hls.destroy();
                break;
              default:
                setError('errors.player.fatalError');
                hls.destroy();
                break;
            }
          }
        });

        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari) — append SAS to manifest URL
        const src = sasToken ? appendSasToken(streamUrl, sasToken) : streamUrl;
        video.src = src;
        video.addEventListener('loadedmetadata', () => {
          setIsLoading(false);
        });
      } else {
        setError('errors.player.unsupported');
        setIsLoading(false);
      }
    }).catch(() => {
      setError('errors.player.loadFailed');
      setIsLoading(false);
    });

    // Video event listeners
    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleDurationChange = () => setDuration(video.duration || 0);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      destroyHls();
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [streamUrl, sasToken, destroyHls]);

  const play = useCallback(() => {
    videoRef.current?.play().catch(() => {
      setError('errors.player.playbackFailed');
    });
  }, []);

  const pause = useCallback(() => {
    videoRef.current?.pause();
  }, []);

  const toggle = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  const seek = useCallback((time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  }, []);

  return {
    videoRef,
    isPlaying,
    currentTime,
    duration,
    isLoading,
    error,
    play,
    pause,
    toggle,
    seek,
  };
}
